import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Divider, Space, Tabs, Table, Typography, Tag } from 'antd';
import { Button, ButtonGroup } from '@mui/material';
import { listCharts, fetchChart } from '../../api/chartsApi';
import {
  selectCharts,
  selectChartsLoading,
  selectCurrentPage,
  selectChartLoadingState,
  selectChartError
} from '../../store/slices/charts.slice';
import PlotlyJsonChart from "../../charts/Plotly/PlotlyJsonChart";
import TimeCell from "../../components/TimeCell";
import { ExternalLink, ArrowUpRight, X } from 'lucide-react';
import GradientBanner from "../../components/Banner/components/GradientBanner";
import NavTrail from "../../layouts/NavTrail";
const { Title, Text } = Typography;

// Table component for listing charts
const ChartsList = ({ onChartSelect }) => {
  const dispatch = useDispatch();
  const charts = useSelector(selectCharts);
  const loading = useSelector(selectChartsLoading);
  const currentPage = useSelector(selectCurrentPage);

  useEffect(() => {
    dispatch(listCharts(0));
  }, [dispatch]);

  const loadMore = () => {
    dispatch(listCharts(currentPage + 1));
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => (
        <Space onClick={() => onChartSelect(text)}>
          <Typography.Link style={{color: "#0171e3"}}>
            {text}
          </Typography.Link>
          <sup>
            <ArrowUpRight size={16} style={{color: '#0171e3'}} />
          </sup>
        </Space>
      ),
    },
    {
      title: 'Metadata',
      dataIndex: 'metadata',
      key: 'metadata',
      render: (metadata) => (
        <Space size={[0, 5]} wrap>
          {Object.entries(metadata || {})
            .filter(([key]) => key !== 'start_date' && key !== 'end_date')
            .map(([key, value]) => (
              <Tag key={key} className={'bg-gradient-to-br from-blue-200 to-blue-50 rounded-xl'} bordered={false}>
                {`${key}: ${value}`}
              </Tag>
            ))}
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => <TimeCell timestamp={new Date(date).getTime() / 1000} timezone="America/New_York" />,
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date) => <TimeCell timestamp={new Date(date).getTime() / 1000} timezone="America/New_York" />,
    },
  ];

  return (
    <Card bordered={false} style={{width: '100%'}}>
      <Table
        columns={columns}
        dataSource={charts}
        rowKey="title"
        loading={loading}
        footer={() => (
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Button variant="contained" color="primary" onClick={loadMore} disabled={loading}>
              {loading ? 'Loading...' : 'Load More'}
            </Button>
          </div>
        )}
        pagination={false}
      />
    </Card>
  );
};

// Detailed chart view component
const ChartDetail = ({ title }) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectChartLoadingState(title));
  const error = useSelector(selectChartError(title));
  const chart = useSelector(selectCharts).find(c => c.title === title);

  useEffect(() => {
    dispatch(fetchChart(title));
  }, [dispatch, title]);

  if (loading) {
    return <Card loading />;
  }

  if (error) {
    return (
      <Card>
        <Text type="danger">Error loading chart: {error}</Text>
      </Card>
    );
  }

  return (
    <Card className={'gapless p-6'} bordered={false}>
      {chart?.plot_data && (
        <PlotlyJsonChart data={chart} />
      )}
    </Card>
  );
};

// Main component that manages the views
const ChartLauncher = () => {
  const [openCharts, setOpenCharts] = useState([]);
  const [activeKey, setActiveKey] = useState('list');

  const handleChartSelect = (title) => {
    if (!openCharts.includes(title)) {
      setOpenCharts([...openCharts, title]);
    }
    setActiveKey(title);
  };

  const handleTabClose = (targetKey) => {
    const newOpenCharts = openCharts.filter(title => title !== targetKey);
    setOpenCharts(newOpenCharts);

    // If we're closing the active tab, switch to the list view
    if (activeKey === targetKey) {
      setActiveKey('list');
    }
  };

  const items = [
    {
      key: 'list',
      label: 'Charts List',
      children: <ChartsList onChartSelect={handleChartSelect} />,
      closable: false
    },
    ...openCharts.map(title => ({
      key: title,
      label: (
        <Space>
          {title}
          <X
            size={16}
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleTabClose(title);
            }}
          />
        </Space>
      ),
      children: <ChartDetail title={title} />,
      closable: true
    }))
  ];

  return (
    <div>
      <NavTrail items={[{label:"Beta", path: '/apps'}, {label:"Charts"}]} />
      <Divider />
        <Tabs
            centered={true}
          activeKey={activeKey}
          onChange={setActiveKey}
          items={items}
          type="card"
        />
    </div>
  );
};

export default ChartLauncher;